import { apiUrl, apiRoutes } from "../configs/parameters";
import axios from "axios";
import {
  showFailureNotification,
  showSuccessNotification,
} from "store/actions/notification";
import { getContentTypesSuccess } from "store/actions/content";
import { finishLoading, startLoading } from "store/actions/loading";
import { sendMessageSuccess } from "store/actions/messages";
import { getAuthenticationToken } from "helpers/localStorage";

const getHeaders = (token, isFormData = false) => ({
  headers: {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `JWT ${token}` } : ""),
    "Application-Name": "GWT-ADMIN",
  },
});

const generateUrlQuery = (params) => {
  if (!params) return "";
  let query = "?";
  for (const key in params) {
    if (params[key]) {
      if (Array.isArray(params[key])) {
        const values = params[key];
        values.forEach((val) => (query += `${key}[]=${val}&`));
      } else {
        query += `${key}=${params[key]}&`;
      }
    }
  }

  return query;
};

const getFullUrl = (route, params) => {
  return `${apiUrl}${apiRoutes[route]}`;
};

const api = axios.create({});

const methods = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  PATCH: "PATCH",
  DELETE: "DELETE",
};

export const createCall = (endpoint, method = methods.GET, data, opt = {}) => {
  const token = getAuthenticationToken();
  const isFormData =
    typeof FormData !== "undefined" && data instanceof FormData;
  const headers = getHeaders(token, isFormData);
  const options = {
    url: endpoint,
    method,
    data,
    ...opt,
    ...headers,
  };
  return api(options);
};

// Trigger a browser download for an in-memory blob (used for zip packages).
const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const proxy = {
  // Generic HTTP methods
  get: (endpoint, params) => {
    return createCall(`${apiUrl}${endpoint}`, methods.GET, null, { params });
  },
  post: (endpoint, data) => {
    return createCall(`${apiUrl}${endpoint}`, methods.POST, data);
  },
  put: (endpoint, data) => {
    return createCall(`${apiUrl}${endpoint}`, methods.PUT, data);
  },
  patch: (endpoint, data) => {
    return createCall(`${apiUrl}${endpoint}`, methods.PATCH, data);
  },
  delete: (endpoint, data) => {
    return createCall(`${apiUrl}${endpoint}`, methods.DELETE, data);
  },

  authenticate: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  uploadRestrictedRegion: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  getRestrictedRegion: (key, params) => {
    const { region_id } = params;
    return createCall(getFullUrl(key) + `/${region_id}`);
  },
  getRestrictedRegions: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  deleteRestrictedRegion: (key, params) => {
    const { region_id } = params;
    return createCall(getFullUrl(key) + `/${region_id}`, methods.DELETE, {});
  },
  updateRestrictedRegion: (key, params) => {
    const { region_id, body } = params;
    return createCall(getFullUrl(key) + `/${region_id}`, methods.PATCH, body);
  },
  getCustomers: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  unlinkAccountAndCustomer: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  linkAccountAndCustomer: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  updateAccount: (key, params) => {
    const { member_id } = params;
    return createCall(`${getFullUrl(key)}/${member_id}`, methods.PUT, params);
  },
  createAccount: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  resetPassword: (key, params) => {
    return createCall(getFullUrl(key), methods.PATCH, params);
  },
  sendPassword: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  resetQuiz: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  getAccount: (key, params) => {
    const { member_id } = params;
    return createCall(`${getFullUrl(key)}/${member_id}`);
  },
  getAccounts: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  deleteAccount: (key, params) => {
    const { member_id } = params;
    return createCall(`${getFullUrl(key)}/${member_id}`, methods.DELETE);
  },
  toggleGame: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  getPermissions: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  togglePermission: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  uploadCSV: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  getActivityLog: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },

  toggleContentCustomer: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  deleteContent: (key, params) => {
    const { content_id } = params;
    return createCall(`${getFullUrl(key)}/${content_id}`, methods.DELETE);
  },

  // Field Clip proxy methods
  getTutorialVideos: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },

  getTutorialVideo: (key, params) => {
    const { tutorial_video_id } = params;
    return createCall(`${getFullUrl(key)}/${tutorial_video_id}`, methods.GET);
  },

  createTutorialVideo: (key, params, formData) => {
    if (formData) {
      return createCall(getFullUrl(key), methods.POST, formData);
    }
    return createCall(getFullUrl(key), methods.POST, params);
  },

  duplicateTutorialVideo: (key, id) => {
    return createCall(`${getFullUrl(key)}/${id}`, methods.POST, {});
  },

  updateTutorialVideo: (key, params, formData) => {
    const { tutorial_video_id } = params;
    if (formData) {
      return createCall(
        `${getFullUrl(key)}/${tutorial_video_id}`,
        methods.PATCH,
        formData
      );
    }
    return createCall(
      `${getFullUrl(key)}/${tutorial_video_id}`,
      methods.PATCH,
      params
    );
  },

  deleteTutorialVideo: (key, params) => {
    const { tutorial_video_id } = params;
    return createCall(
      `${getFullUrl(key)}/${tutorial_video_id}`,
      methods.DELETE
    );
  },

  getTutorialVideoCustomers: (key, params) => {
    const { tutorial_video_id } = params;
    return createCall(
      `${getFullUrl(key)}/${tutorial_video_id}/customers`,
      methods.GET
    );
  },

  // Environmental Tailgate proxy methods
  getEnvironmentalTailgates: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },

  getEnvironmentalTailgate: (key, params) => {
    const { environmental_tailgate_id } = params;
    return createCall(
      `${getFullUrl(key)}/${environmental_tailgate_id}`,
      methods.GET
    );
  },

  createEnvironmentalTailgate: (key, params, formData) => {
    if (formData) {
      return createCall(getFullUrl(key), methods.POST, formData);
    }
    return createCall(getFullUrl(key), methods.POST, params);
  },

  duplicateEnvironmentalTailgate: (key, id) => {
    return createCall(`${getFullUrl(key)}/${id}`, methods.POST, {});
  },

  updateEnvironmentalTailgate: (key, params, formData) => {
    const { environmental_tailgate_id } = params;
    if (formData) {
      return createCall(
        `${getFullUrl(key)}/${environmental_tailgate_id}`,
        methods.PATCH,
        formData
      );
    }
    return createCall(
      `${getFullUrl(key)}/${environmental_tailgate_id}`,
      methods.PATCH,
      params
    );
  },

  deleteEnvironmentalTailgate: (key, params) => {
    const { environmental_tailgate_id } = params;
    return createCall(
      `${getFullUrl(key)}/${environmental_tailgate_id}`,
      methods.DELETE
    );
  },

  getEnvironmentalTailgateCustomers: (key, params) => {
    const { environmental_tailgate_id } = params;
    return createCall(
      `${getFullUrl(key)}/${environmental_tailgate_id}/customers`,
      methods.GET
    );
  },

  toggleEnvironmentalTailgateCustomer: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  // Survey proxy methods
  getSurveys: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },

  getSurvey: (key, params) => {
    const { survey_id } = params;
    return createCall(`${getFullUrl(key)}/${survey_id}`, methods.GET);
  },

  createSurvey: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  duplicateSurvey: (key, id) => {
    return createCall(`${getFullUrl(key)}/${id}`, methods.POST, {});
  },

  updateSurvey: (key, params) => {
    const { survey_id, ...data } = params;
    return createCall(`${getFullUrl(key)}/${survey_id}`, methods.PATCH, data);
  },

  deleteSurvey: (key, params) => {
    const { survey_id } = params;
    return createCall(`${getFullUrl(key)}/${survey_id}`, methods.DELETE);
  },

  getSurveyCustomers: (key, params) => {
    const { survey_id } = params;
    return createCall(`${getFullUrl(key)}/${survey_id}/customers`, methods.GET);
  },

  toggleSurveyCustomer: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  getSurveysReport: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },

  getEnvironmentalTailgatesReport: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },

  getTutorialVideosReport: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },

  // VMU course proxy methods
  getVmuCourses: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },

  getVmuCourse: (key, params) => {
    const { course_id } = params;
    return createCall(`${getFullUrl(key)}/${course_id}`, methods.GET);
  },

  createVmuCourse: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  updateVmuCourse: (key, params) => {
    const { course_id, ...data } = params;
    return createCall(`${getFullUrl(key)}/${course_id}`, methods.PATCH, data);
  },

  deleteVmuCourse: (key, params) => {
    const { course_id } = params;
    return createCall(`${getFullUrl(key)}/${course_id}`, methods.DELETE);
  },

  getVmuCourseAccounts: (key, params) => {
    const { course_id } = params;
    return createCall(`${getFullUrl(key)}/${course_id}/accounts`, methods.GET);
  },

  toggleVmuCourseAccount: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  // VMU customers / settings proxy methods
  getVmuCustomers: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },

  getVmuCustomer: (key, params) => {
    const { customer_id } = params;
    return createCall(`${getFullUrl(key)}/${customer_id}`, methods.GET);
  },

  updateVmuSettings: (key, params) => {
    const { customer_id, ...data } = params;
    return createCall(`${getFullUrl(key)}/${customer_id}`, methods.PATCH, data);
  },

  // VMU resources proxy methods
  getVmuResources: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },

  createVmuResource: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  updateVmuResource: (key, params) => {
    const { resource_id, ...data } = params;
    return createCall(`${getFullUrl(key)}/${resource_id}`, methods.PATCH, data);
  },

  deleteVmuResource: (key, params) => {
    const { resource_id } = params;
    return createCall(`${getFullUrl(key)}/${resource_id}`, methods.DELETE);
  },

  getPlants: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },

  getQuizContents: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  getQuizContent: (key, params) => {
    const { quiz_content_id } = params;
    return createCall(`${getFullUrl(key)}/${quiz_content_id}`);
  },

  getLeaderboard: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  duplicateQuizQuestion: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  getQuizQuestions: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  updateQuizQuestion: (key, params) => {
    return createCall(getFullUrl(key), methods.PUT, params);
  },
  getPlants: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  createQuizQuestion: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  updateQuizAnswer: (key, params) => {
    const { quiz_answer_id } = params;
    return createCall(
      `${getFullUrl(key)}/${quiz_answer_id}`,
      methods.PUT,
      params
    );
  },
  createQuizAnswer: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  getQuizQuestionAnswers: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  getQuizList: (key, { customer_id }) => {
    return createCall(`${getFullUrl(key)}/${customer_id}`);
  },
  addOrUpdateQuiz: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  readMessageThread: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  createMessage: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  forwardMessage: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  getPlants: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },

  deletePlant: (key, params) => {
    const { plant_id } = params;
    return createCall(`${getFullUrl(key)}/${plant_id}`, methods.DELETE);
  },

  updatePlant: (key, params) => {
    return createCall(getFullUrl(key), methods.PUT, params);
  },
  createPlant: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  getPlant: (key, params) => {
    const { plant_id } = params;
    return createCall(`${getFullUrl(key)}/${plant_id}`);
  },

  unlinkRegionAndPlant: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  linkRegionAndPlant: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  getPlantRegions: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  getRegions: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  deleteRegion: (key, params) => {
    const { eco_region_id } = params;
    return createCall(`${getFullUrl(key)}/${eco_region_id}`, methods.DELETE);
  },
  getRegion: (key, params) => {
    const { eco_region_id } = params;
    return createCall(`${getFullUrl(key)}/${eco_region_id}`, methods.GET);
  },
  updateRegion: (key, params) => {
    return createCall(getFullUrl(key), methods.PUT, params);
  },
  createRegion: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  notify: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  uploadImages: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  getPlantSections: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  updatePlantSection: (key, params) => {
    const { section_id } = params;
    return createCall(`${getFullUrl(key)}/${section_id}`, methods.PUT, params);
  },
  deletePlantSection: (key, params) => {
    const { section_id } = params;
    return createCall(`${getFullUrl(key)}/${section_id}`, methods.DELETE);
  },
  getContents: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  getContentsByType: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  getContentTypes: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  getContent: (key, params) => {
    const { content_id } = params;
    return createCall(`${getFullUrl(key)}/${content_id}`);
  },
  createContent: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  updateContent: (key, params) => {
    const { content_id, created_at, ...body } = params;
    return createCall(`${getFullUrl(key)}/${content_id}`, methods.PUT, body);
  },
  deleteImage: (key, params) => {
    const { section_image_id, content_image_id } = params;

    return createCall(
      `${getFullUrl(key)}/${section_image_id || content_image_id}`,
      methods.DELETE,
      params
    );
  },
  updateImageInfo: (key, params) => {
    return createCall(getFullUrl(key), methods.PUT, params);
  },

  createCustomer: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  deleteCustomer: (key, params) => {
    const { customer_id } = params;
    return createCall(`${getFullUrl(key)}/${customer_id}`, methods.DELETE);
  },
  getCustomer: (key, params) => {
    const { customer_id } = params;
    return createCall(`${getFullUrl(key)}/${customer_id}`);
  },
  updateCustomer: (key, params) => {
    return createCall(getFullUrl(key), methods.PUT, params);
  },
  getPlantCustomers: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  getTutorialVideoCustomers: (key, params) => {
    return createCall(
      `${getFullUrl(key)}/${params.tutorial_video_id}/customers`,
      methods.GET
    );
  },
  unlinkPlantFromCustomer: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  linkPlantToCustomer: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  toggleTutorialVideoCustomer: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  duplicatePlant: (key, params) => {
    const { plant_id } = params;
    return createCall(`${getFullUrl(key)}/${plant_id}`, methods.POST);
  },
  getConversations: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  deleteMessage: (key, params) => {
    const { message_id } = params;
    return createCall(`${getFullUrl(key)}/${message_id}`, methods.DELETE, {});
  },
  getNotificationImage: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  saveNotificationDecision: (key, params) => {
    const { id, body } = params;
    return createCall(`${getFullUrl(key)}/${id}/decision`, methods.PATCH, body);
  },
  deleteNotificationImage: (key, params) => {
    const { id } = params;
    return createCall(`${getFullUrl(key)}/${id}`, methods.DELETE);
  },

  createPlantSection: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  createQuizContent: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  updateQuizContent: (key, params) => {
    return createCall(getFullUrl(key), methods.PATCH, params);
  },

  deleteQuizContent: (key, params) => {
    return createCall(getFullUrl(key), methods.DELETE, params);
  },
  getQuizQuestion: (key, { quiz_question_id }) => {
    return createCall(`${getFullUrl(key)}/${quiz_question_id}`, methods.GET);
  },

  linkQuizQuestionAndCustomer: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  unlinkQuizQuestionAndCustomer: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  updateQuizQuestionFeedback: (key, params) => {
    return createCall(getFullUrl(key), methods.PUT, params);
  },

  createQuizQuestionFeedback: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  deleteQuizQuestion: (key, params) => {
    return createCall(getFullUrl(key), methods.DELETE, params);
  },

  getPlantdexSubmissions: (key, params) => {
    return createCall(`${getFullUrl(key)}`, methods.GET, null, { params });
  },

  // Tag methods
  getTags: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  getTag: (key, params) => {
    const { tag_id } = params;
    return createCall(`${getFullUrl(key)}/${tag_id}`);
  },
  createTag: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  updateTag: (key, params) => {
    const { tag_id, ...body } = params;
    return createCall(`${getFullUrl(key)}/${tag_id}`, methods.PATCH, body);
  },
  deleteTag: (key, params) => {
    const { tag_id } = params;
    return createCall(`${getFullUrl(key)}/${tag_id}`, methods.DELETE);
  },
  assignTagToAccount: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },
  getTagsByAccount: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  getTagsForAccountAndCustomer: (key, params) => {
    return createCall(getFullUrl(key), methods.GET, null, { params });
  },
  batchUpdateAccountTags: (key, params) => {
    return createCall(getFullUrl(key), methods.POST, params);
  },

  getCustomerPlantSectionTypeOrder: (key, params) => {
    const { customer_id } = params;
    return createCall(`${getFullUrl(key)}/${customer_id}`);
  },

  updateCustomerPlantSectionTypeOrder: (key, params) => {
    const { customer_id, ...body } = params;
    return createCall(`${getFullUrl(key)}/${customer_id}`, methods.PUT, body);
  },
};

export const ResponseHandler = {
  // Default handler for direct HTTP methods
  defaultHandler: async (response) => {
    return {
      data: response,
      success: true,
    };
  },
  //
  uploadRestrictedRegion: async (response) => {
    const { result, success } = response;
    return {
      data: result,
      success,
    };
  },
  getRestrictedRegion: async (response) => {
    const { result, success } = response;

    return {
      data: result,
      success,
    };
  },
  getRestrictedRegions: async (response) => {
    const { result, success } = response;
    return { data: result, success };
  },
  deleteRestrictedRegion: async (response) => {
    const { result, success } = response;
    return {
      data: result,
      success,
    };
  },
  updateRestrictedRegion: async (response) => {
    const { result, success } = response;
    return {
      data: result,
      success,
    };
  },
  getCustomers: async (response) => {
    return {
      data: response?.result?.customers,
      success: true,
    };
  },
  getRegion: async (response) => {
    return {
      data: response?.result,
      success: true,
    };
  },
  getPlantRegions: async (response) => {
    const { result, success } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't get regions list!",
      };
    }

    return {
      success: true,
      data: result?.regions || [],
      page: {
        current: result?.page || 1,
        count: result?.count || 0,
        offset: result?.offset || 0,
      },
    };
  },
  getRegions: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't get regions list!",
      };
    }

    return {
      data: result?.regions || [],
      page: {
        current: result?.page || 1,
        count: result?.count || 0,
        offset: result?.offset || 0,
      },
      success: true,
    };
  },
  unlinkRegionAndPlant: async (response) => {
    const { success } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't unlink  eco region and plant!",
      };
    }

    return {
      data: success,
      success: true,
      message: "Successfully unlinked",
    };
  },
  linkRegionAndPlant: async (response) => {
    const { success } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't link eco region and plant!",
      };
    }

    return {
      data: success,
      success: true,
      message: "Successfully linked",
    };
  },
  createRegion: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't create region!",
      };
    }

    return {
      data: result.eco_region_id,
      success: true,
      message: "Created new region!",
    };
  },
  deleteRegion: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't delete region!",
      };
    }

    return {
      data: result?.eco_region_id,
      success: true,
      message: "Successfully deleted!",
    };
  },
  updateRegion: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't update region!",
      };
    }

    return {
      data: result.eco_region_id,
      success: true,
      message: "Successfully updated",
    };
  },
  getAccount: async (response) => {
    return {
      data: response.result,
      success: true,
    };
  },
  getAccounts: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't get accounts list!",
      };
    }

    return {
      data: result?.accounts || [],
      ...result,
      success: true,
    };
  },
  createAccount: async (response) => {
    const { success, result, error } = response;
    if (!success) {
      return {
        success: false,
        message: error?.message || "Can't create account!",
      };
    }

    return {
      data: result,
      success: true,
      message: "Created new account!",
    };
  },
  deleteAccount: async (response) => {
    const { success, result, error } = response;
    if (!success) {
      return {
        success: false,
        message: error?.message || "Can't delete account!",
      };
    }

    return {
      data: result.member_id,
      success: true,
      message: "Successfully deleted!",
    };
  },
  updateAccount: async (response) => {
    const { success, result, error } = response;
    if (!success) {
      return {
        success: false,
        message: error?.message || "Can't update account!",
      };
    }

    return {
      data: result.member_id,
      success: true,
      message: "Successfully updated",
    };
  },
  resetPassword: async (response) => {
    const { success, error } = response;
    if (!success) {
      return {
        success: false,
        message: error?.message || "Can't reset password!",
      };
    }

    // API returns the body directly when it already has a `success` field
    // (response.interceptor passes it through). Surface the whole body so the
    // UI can read `password` / `email_sent`.
    return {
      data: response,
      success: true,
      message: "Successfully done!",
    };
  },
  sendPassword: async (response) => {
    const { success, error } = response;
    if (!success) {
      return {
        success: false,
        message: error?.message || "Failed to send email",
      };
    }
    return { success: true, message: "Email sent" };
  },
  getPlant: async (response) => {
    return {
      data: response.result,
      success: true,
    };
  },
  getPlants: async (response) => {
    console.log("API Response:", response); // Ensure this logs the correct response
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't get plants list!",
      };
    }

    return {
      data: result,
      success: true,
    };
  },
  createPlant: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't create plant!",
      };
    }

    return {
      data: result.plant_id,
      success: true,
      message: "Created new plant, Section and Customer tabs are enabled!",
    };
  },
  deletePlant: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't delete plant!",
      };
    }

    return {
      data: result.plant_id,
      success: true,
      message: "Successfully deleted!",
    };
  },
  updatePlant: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't update plant!",
      };
    }

    return {
      data: result.plant_id,
      success: true,
      message: "Successfully updated",
    };
  },
  uploadImages: async (response) => {
    const { success } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't upload image!",
      };
    }

    return {
      data: success,
      success: true,
      message: "Successfully uploaded",
    };
  },
  uploadCSV: async (response) => {
    const { success, rejected, result } = response;

    if (!success) {
      return {
        success: false,
        message: "Can't upload csv!",
      };
    }

    return {
      data: result,
      rejected,
      success: true,
      message: "Successfully uploaded",
    };
  },
  unlinkAccountAndCustomer: async (response) => {
    const { success } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't unlink account!",
      };
    }

    return {
      data: success,
      success: true,
      message: "Successfully unlinked",
    };
  },
  linkAccountAndCustomer: async (response) => {
    const { success } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't link account!",
      };
    }

    return {
      data: success,
      success: true,
      message: "Successfully linked",
    };
  },
  deleteContent: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't delete content!",
      };
    }

    return {
      data: result.content_id,
      success: true,
      message: "Successfully deleted!",
    };
  },

  toggleContentCustomer: async (response) => {
    const { success } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't unlink / link customer!",
      };
    }

    return {
      data: success,
      success: true,
      message: "Successfully linked",
    };
  },

  // QUIZ stuff
  getQuizQuestion: async (response) => {
    return {
      data: response.result,
      success: true,
    };
  },
  getQuizList: async (response) => {
    const { result } = response;
    return {
      data: result,
      success: true,
    };
  },
  addOrUpdateQuiz: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't update / create quiz!",
      };
    }

    return {
      data: result,
      success: true,
      message: "Successful request",
    };
  },
  getQuizQuestions: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't get quiz question list!",
      };
    }

    return {
      data: result,
      success: true,
    };
  },
  createQuizQuestion: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't create quiz question!",
      };
    }

    return {
      data: result.quiz_question_id,
      success: true,
      message:
        "Created new quiz question, Answer and Customer tabs are enabled!",
    };
  },
  deleteQuizQuestion: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't delete quiz question!",
      };
    }

    return {
      data: result.quiz_question_id,
      success: true,
      message: "Successfully deleted!",
    };
  },
  updateQuizQuestion: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't update quiz question!",
      };
    }

    return {
      data: result.quiz_question_id,
      success: true,
      // message: "Successfully updated",
    };
  },
  unlinkQuizQuestionAndCustomer: async (response) => {
    const { success } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't unlink customer!",
      };
    }

    return {
      data: success,
      success: true,
      message: "Successfully unlinked",
    };
  },
  linkQuizQuestionAndCustomer: async (response) => {
    const { success } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't update!",
      };
    }

    return {
      data: success,
      success: true,
      message: "Successfully updated",
    };
  },

  getLeaderboard: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't get Leaderboard",
      };
    }

    return {
      data: result.data,
      quiz: result.quiz,
      sorting: result.sorting,
      success: true,
    };
  },

  resetQuiz: async (response) => {
    const { success, error } = response;
    const message = error?.message ? error.message : "Can't reset quiz";
    return {
      success,
      message: success ? "Successful request" : message,
    };
  },

  // Quiz answers
  getQuizAnswer: async (response) => ({
    data: response.result,
    success: true,
  }),

  getQuizQuestionAnswers: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't get quiz answers list!",
      };
    }

    return {
      data: result,
      success: true,
    };
  },
  createQuizAnswer: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't create quiz answer!",
      };
    }

    return {
      data: result.quiz_answer_id,
      success: true,
      message: "Successfully created!",
    };
  },
  deleteQuizAnswer: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't delete quiz answer!",
      };
    }

    return {
      data: result.quiz_answer_id,
      success: true,
      message: "Successfully deleted!",
    };
  },
  updateQuizAnswer: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't update quiz answer!",
      };
    }

    return {
      data: result.quiz_answer_id,
      success: true,
      message: "Successfully updated",
    };
  },

  // Quiz Question Feedback
  getQuizQuestionFeedback: async (response) => ({
    data: response.result,
    success: true,
  }),
  createQuizQuestionFeedback: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't create quiz question feedback!",
      };
    }

    return {
      data: result.quiz_question_feedback_id,
      success: true,
      message: "Successfully created!",
    };
  },
  deleteQuizQuestionFeedback: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't delete quiz question feedback!",
      };
    }

    return {
      data: result.quiz_question_feedback_id,
      success: true,
      message: "Successfully deleted!",
    };
  },
  updateQuizQuestionFeedback: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't update quiz question feedback!",
      };
    }

    return {
      data: result.quiz_question_feedback_id,
      success: true,
      // message: "Successfully updated",
    };
  },

  // Quiz Content
  linkUnlinkQuizContentCustomer: async (response) => {
    const { success } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't make request!",
      };
    }

    return {
      data: success,
      success: true,
      message: "Done",
    };
  },

  createQuizContent: async (response) => ({
    success: response.success,
    message: response.success ? "Created!" : "Can't create.",
    data: response.result,
  }),

  updateQuizContent: async (response) => ({
    success: response.success,
    message: response.success ? "Updated!" : "Can't update data.",
    data: response.result,
  }),

  deleteQuizContent: async (response) => ({
    success: response.success,
    message: response.success ? "Deleted!" : "Can't delete data.",
  }),

  getQuizContent: async (response) => ({
    success: response.success,
    message: response.success ? null : "Can't fetch data.",
    data: response.result,
  }),

  getQuizContents: async (response) => ({
    success: response.success,
    message: response.success ? null : "Can't fetch data.",
    data: response.result,
  }),

  getCustomerPlantSectionTypeOrder: async (response) => {
    const success = response?.success || false;
    const result = response?.result || [];

    return {
      data: result,
      success,
    };
  },

  updateCustomerPlantSectionTypeOrder: async (response) => {
    const { result, success } = response;
    return {
      data: result || {},
      success: success || false,
    };
  },

  // Messages
  createMessage: async ({ success, result }, dispatcher) => {
    if (success) {
      dispatcher(sendMessageSuccess(result?.[0]));
    }

    return {
      success,
      data: result,
    };
  },

  deleteMessage: async ({ success, result }, dispatcher) => {
    return {
      success,
      data: result,
    };
  },

  getNotificationImage: async ({ success, result }, dispatcher) => {
    return {
      success,
      data: result,
    };
  },

  saveNotificationDecision: async ({ success, result }) => {
    return {
      success,
      data: result,
      message: success ? "Decision saved" : "Unable to save decision",
    };
  },

  deleteNotificationImage: async ({ success, error }) => {
    return {
      success,
      message: success
        ? "Request deleted"
        : error?.message || "Unable to delete request",
    };
  },

  forwardMessage: async ({ success, result }) => ({
    success,
    data: result,
  }),

  getConversations: async (response) => ({
    success: response.success,
    message: response.success ? null : "Can't get conversation list.",
    data: response.result,
  }),

  readMessageThread: async (response) => ({
    success: response.success,
    message: response.success ? null : "Can't mark as read.",
  }),

  notify: async (response) => ({
    success: response.success,
    message: response.success ? null : "Unable to send push notification.",
  }),

  toggleGame: async (response) => ({
    success: response.success,
    message: response.success ? null : "Can't enable / disable game.",
  }),

  duplicateQuizQuestion: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't duplicate the question!",
      };
    }

    return {
      data: result.quiz_question_id,
      success: true,
      message: "The question has been duplicated successfully",
    };
  },

  // Activity log
  getActivityLog: async (response) => ({
    data: response.result,
    success: response.success,
    message: response.error?.message,
  }),
  getPermissions: async (response) => {
    return {
      data: response?.result,
      success: response.success,
    };
  },
  togglePermission: async (response) => {
    const { success } = response;
    if (!success) {
      return {
        success: false,
        message: " Failed to change permission status!",
      };
    }

    return {
      data: success,
      success: true,
      message: "Permission status changed!",
    };
  },

  getPlantdexSubmissions: async (response) => {
    const { result, success } = response;
    return { data: result, success };
  },

  // Tag response handlers
  getTags: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't get tags list!",
      };
    }

    return {
      data: Array.isArray(result) ? result : result?.tags || [],
      success: true,
    };
  },
  getTag: async (response) => {
    return {
      data: response.result,
      success: true,
    };
  },
  createTag: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't create tag!",
      };
    }

    return {
      data: result.tag_id,
      success: true,
      message: "Created new tag!",
    };
  },
  updateTag: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't update tag!",
      };
    }

    return {
      data: result.tag_id,
      success: true,
      message: "Successfully updated",
    };
  },
  deleteTag: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't delete tag!",
      };
    }

    return {
      data: result.tag_id,
      success: true,
      message: "Successfully deleted!",
    };
  },
  assignTagToAccount: async (response) => {
    const { success } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't assign tag to user!",
      };
    }

    return {
      success: true,
      message: "Successfully assigned tag",
    };
  },
  getTagsByAccount: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't get user tags!",
      };
    }

    return {
      data: result?.tags || [],
      success: true,
    };
  },
  getTagsForAccountAndCustomer: async (response) => {
    const { success, result } = response;
    if (!success) {
      return {
        success: false,
        message: "Can't get tags for account and customer!",
      };
    }

    return {
      data: {
        userTags: result?.userTags || [],
        availableTags: result?.availableTags || [],
      },
      success: true,
    };
  },
  batchUpdateAccountTags: async (response) => {
    const { success, message } = response;
    if (!success) {
      return {
        success: false,
        message: message || "Failed to update tags!",
      };
    }

    return {
      success: true,
      message: message || "Tags updated successfully",
    };
  },

  // Tutorial video handlers
  getTutorialVideos: async (response) => {
    return {
      data: response,
      success: true,
    };
  },

  getTutorialVideo: async (response) => {
    return {
      data: response,
      success: true,
    };
  },

  createTutorialVideo: async (response) => {
    return {
      data: response,
      success: true,
      message: "Tutorial video created successfully",
    };
  },

  updateTutorialVideo: async (response) => {
    return {
      data: response,
      success: true,
      message: "Tutorial video updated successfully",
    };
  },

  deleteTutorialVideo: async (response) => {
    return {
      data: response,
      success: true,
      message: "Tutorial video deleted successfully",
    };
  },

  // Environmental Tailgate handlers
  getEnvironmentalTailgates: async (response) => {
    return {
      data: response,
      success: true,
    };
  },

  getEnvironmentalTailgate: async (response) => {
    return {
      data: response,
      success: true,
    };
  },

  createEnvironmentalTailgate: async (response) => {
    return {
      data: response,
      success: true,
      message: "Field note created successfully",
    };
  },

  updateEnvironmentalTailgate: async (response) => {
    return {
      data: response,
      success: true,
      message: "Field note updated successfully",
    };
  },

  deleteEnvironmentalTailgate: async (response) => {
    return {
      data: response,
      success: true,
      message: "Field note deleted successfully",
    };
  },

  // Tutorial video presigned URL handlers
  getPresignedUrl: async (response) => {
    return {
      data: response,
      success: true,
    };
  },

  getMultipartUploadUrls: async (response) => {
    return {
      data: response,
      success: true,
    };
  },

  completeMultipartUpload: async (response) => {
    return {
      data: response,
      success: true,
    };
  },

  registerUploadedFile: async (response) => {
    return {
      data: response,
      success: true,
      message: "File registered successfully",
    };
  },

  // Survey handlers
  getSurveys: async (response) => {
    return {
      data: response,
      success: true,
    };
  },

  getSurvey: async (response) => {
    return {
      data: response,
      success: true,
    };
  },

  createSurvey: async (response) => {
    return {
      data: response,
      success: true,
      message: "Field bite created successfully",
    };
  },

  updateSurvey: async (response) => {
    return {
      data: response,
      success: true,
      message: "Field bite updated successfully",
    };
  },

  deleteSurvey: async (response) => {
    return {
      data: response,
      success: true,
      message: "Field bite deleted successfully",
    };
  },

  getSurveyCustomers: async (response) => {
    return {
      data: response,
      success: true,
    };
  },
};

export const makeAPICall = async (
  key,
  params,
  dispatcher = null,
  options = { loader: true }
) => {
  try {
    if (options.loader && dispatcher) {
      dispatcher(startLoading());
    }

    // const token = getAuthenticationToken();
    const response = await proxy[key](key, params);

    // Use the specific handler if available, otherwise use the default handler
    const handler = ResponseHandler[key] || ResponseHandler.defaultHandler;
    const { data, success, message, ...props } = await handler(
      response?.data,
      dispatcher
    );

    if (message && dispatcher) {
      if (!success) {
        dispatcher(showFailureNotification(message));
      } else {
        dispatcher(showSuccessNotification(message));
      }
    }

    if (options.loader && dispatcher) {
      dispatcher(finishLoading());
    }
    return {
      data,
      success,
      ...props,
    };
  } catch (error) {
    // console.log("resKey: ", key)

    // console.log(error)
    console.log("Error: ------ ", error, key);
    dispatcher(finishLoading());
    dispatcher(
      showFailureNotification(error.message || "Something went wrong")
    );

    return {
      data: null,
      success: false,
    };
  }
};

const ApiService = {
  getPlantSections: async (params, token) => {
    try {
      const response = await proxy.getPlantSections("getPlantSections", params);
      return response?.data?.result?.plantSections;
    } catch (error) {
      if (error?.data?.error) {
        alert(error?.data?.error.message);
      }
    }
  },
  createPlantSection: async (params, dispatch, token) => {
    let notification = {};

    try {
      const response = await proxy.createPlantSection(
        "createPlantSection",
        params
      );
      const { result, success } = response.data;
      if (success) {
        notification = dispatch(
          showSuccessNotification("Section created successfully!")
        );
      } else {
        notification = dispatch(
          showFailureNotification("Failed to create section!")
        );
      }

      return result?.section_id;
    } catch (error) {
      if (error?.data?.error) {
        alert(error?.data?.error.message);
      }
    }
  },
  updatePlantSection: async (params, dispatch, token) => {
    try {
      const response = await proxy.updatePlantSection(
        "updatePlantSection",
        params
      );
      const { result, success } = response.data;
      let notification = {};
      if (success) {
        notification = dispatch(
          showSuccessNotification("Section updated successfully!")
        );
      } else {
        notification = dispatch(
          showFailureNotification("Failed to update section!")
        );
      }

      return result?.section_id;
    } catch (error) {
      if (error?.data?.error) {
        alert(error?.data?.error.message);
      }
    }
  },
  deletePlantSection: async (params, token) => {
    try {
      const response = await proxy.deletePlantSection(
        "deletePlantSection",
        params
      );
      const { result } = response?.data;
      return result?.section_id;
    } catch (error) {
      if (error?.data?.error) {
        alert(error?.data?.error.message);
      }
    }
  },
  getContents: async (params, token) => {
    try {
      const response = await proxy.getContents("getContents", params);
      return response?.data?.result;
    } catch (error) {
      if (error?.data?.error) {
        alert(error?.data?.error.message);
      }
    }
  },
  getContentsByType: async (params, token) => {
    try {
      const response = await proxy.getContentsByType(
        "getContentsByType",
        params
      );
      return response?.data?.result.contents;
    } catch (error) {
      console.log("Error: ------ ", error);
      return [];
    }
  },
  getContentTypes: async (token, dispatch) => {
    try {
      const response = await proxy.getContentTypes("getContentTypes", {});

      const { result, success } = response.data;
      if (success) {
        dispatch(getContentTypesSuccess(result.contentTypes));
      }
    } catch (error) {
      if (error?.data?.error) {
        alert(error?.data?.error.message);
      }
    }
  },
  getContent: async (params, dispatch) => {
    try {
      const response = await proxy.getContent("getContent", params);

      const { result, success } = response.data;
      if (!success) {
        return dispatch(showFailureNotification("Unable to get content!"));
      }

      return result;
    } catch (error) {
      dispatch(showFailureNotification(`Erros: ${JSON.stringify(error)}`));
      console.log(error);
    }
  },
  createContent: async (params, dispatch) => {
    try {
      const response = await proxy.createContent("createContent", params);
      const { result, success } = response?.data;
      if (!success) {
        return dispatch(showFailureNotification(`Error while create content`));
      }
      dispatch(showSuccessNotification(`Successfully created`));
      return result.content_id;
    } catch (error) {
      if (error?.data?.error) {
        alert(error?.data?.error.message);
      }
      dispatch(showFailureNotification(`Error: ${JSON.stringify(error)}`));
    }
  },
  updateContent: async (params, dispatch) => {
    try {
      const response = await proxy.updateContent("updateContent", params);
      const { result, success } = response?.data;
      if (!success) {
        return dispatch(
          showFailureNotification(`Error while updating content`)
        );
      }
      dispatch(showSuccessNotification(`Successfully updated`));
      return result.content_id;
    } catch (error) {
      if (error?.data?.error) {
        alert(error?.data?.error.message);
      }
      dispatch(showFailureNotification(`Error: ${JSON.stringify(error)}`));
    }
  },

  uploadSpecGuidePackage: async (content_id, file, dispatch) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await createCall(
        `${apiUrl}/v1/content/${content_id}/upload-package`,
        methods.POST,
        formData
      );
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to upload package"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Package uploaded successfully"));
      return response.data?.result || null;
    } catch (error) {
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to upload package"
          }`
        )
      );
      return null;
    }
  },
  downloadSpecGuidePackage: async (content_id, dispatch) => {
    try {
      const response = await createCall(
        `${apiUrl}/v1/content/${content_id}/download-package`,
        methods.GET,
        null,
        { responseType: "blob" }
      );
      triggerBlobDownload(response.data, `spec-guide-${content_id}.zip`);
    } catch (error) {
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to download package"
          }`
        )
      );
    }
  },
  deleteImage: async (params, dispatch, urlKey) => {
    try {
      const response = await proxy.deleteImage(urlKey, params);
      const { success } = response?.data;
      if (!success) {
        return dispatch(showFailureNotification(`Error while deleting image`));
      }
      dispatch(showSuccessNotification(`Deleted`));
    } catch (error) {
      if (error?.data?.error) {
        alert(error?.data?.error.message);
      }
      dispatch(showFailureNotification(`Error: ${JSON.stringify(error)}`));
    }
  },
  updateImageInfo: async (params, dispatch, urlKey) => {
    try {
      const response = await proxy.updateImageInfo(urlKey, params);
      const { success } = response?.data;
      if (!success) {
        return dispatch(showFailureNotification(`Error while updating image`));
      }
      dispatch(showSuccessNotification(`Updated!!`));
    } catch (error) {
      if (error?.data?.error) {
        alert(error?.data?.error.message);
      }
      dispatch(showFailureNotification(`Error: ${JSON.stringify(error)}`));
    }
  },
  createCustomer: async (params, dispatch) => {
    try {
      const response = await proxy.createCustomer("createCustomer", params);
      const { result, success } = response?.data;
      if (!success) {
        return dispatch(showFailureNotification(`Error while create customer`));
      }
      dispatch(showSuccessNotification(`Successfully created`));
      return result.customer_id;
    } catch (error) {
      if (error?.data?.error) {
        alert(error?.data?.error.message);
      }
      dispatch(showFailureNotification(`Error: ${JSON.stringify(error)}`));
    }
  },
  deleteCustomer: async (params, dispatch) => {
    try {
      const response = await proxy.deleteCustomer("deleteCustomer", params);
      const { success } = response.data;
      if (!success) {
        return dispatch(
          showFailureNotification(`Error while deleting customer`)
        );
      }
      dispatch(showSuccessNotification(`Deleted`));
    } catch (error) {
      if (error?.data?.error) {
        alert(error?.data?.error.message);
      }
    }
  },
  getCustomer: async (params, dispatch) => {
    try {
      const response = await proxy.getCustomer("getCustomer", params);

      const { result, success } = response.data;
      if (!success) {
        return dispatch(showFailureNotification("Unable to get customer!"));
      }

      return result;
    } catch (error) {
      dispatch(showFailureNotification(`Erros: ${JSON.stringify(error)}`));
      console.log(error);
    }
  },
  updateCustomer: async (params, dispatch) => {
    try {
      const response = await proxy.updateCustomer("updateCustomer", params);
      const { result, success } = response?.data;
      if (!success) {
        return dispatch(
          showFailureNotification(`Error while updating customer`)
        );
      }
      // dispatch(showSuccessNotification(`Successfully updated`));
      return result.customer_id;
    } catch (error) {
      if (error?.data?.error) {
        alert(error?.data?.error.message);
      }
      dispatch(showFailureNotification(`Error: ${JSON.stringify(error)}`));
    }
  },
  getCustomers: async (params, dispatch) => {
    try {
      const response = await proxy.getCustomers("getCustomers", params);

      const { result, success } = response.data;
      if (!success) {
        return dispatch(
          showFailureNotification("Unable to get customers list!")
        );
      }

      return result;
    } catch (error) {
      dispatch(showFailureNotification(`Erros: ${JSON.stringify(error)}`));
      console.log(error);
    }
  },
  getPlantCustomers: async (params, dispatch) => {
    try {
      const response = await proxy.getPlantCustomers(
        "getPlantCustomers",
        params
      );

      const { result, success } = response.data;
      if (!success) {
        return dispatch(
          showFailureNotification("Unable to get customers list!")
        );
      }

      return result.customers;
    } catch (error) {
      dispatch(showFailureNotification(`Erros: ${JSON.stringify(error)}`));
      console.log(error);
    }
  },
  unlinkPlantFromCustomer: async (params, dispatch) => {
    try {
      const response = await proxy.unlinkPlantFromCustomer(
        "unlinkPlantFromCustomer",
        params
      );

      const { success } = response.data;
      if (!success) {
        return dispatch(
          showFailureNotification("Unable to unlink customer and plant!")
        );
      }

      return success;
    } catch (error) {
      dispatch(showFailureNotification(`Erros: ${JSON.stringify(error)}`));
      console.log(error);
    }
  },
  linkPlantToCustomer: async (params, dispatch) => {
    try {
      const response = await proxy.linkPlantToCustomer(
        "linkPlantToCustomer",
        params
      );

      const { success } = response.data;
      if (!success) {
        return dispatch(
          showFailureNotification("Unable to unlink customer and plant!")
        );
      }

      return success;
    } catch (error) {
      dispatch(showFailureNotification(`Erros: ${JSON.stringify(error)}`));
      console.log(error);
    }
  },
  toggleTutorialVideoCustomer: async (params, dispatch) => {
    try {
      const response = await proxy.toggleTutorialVideoCustomer(
        "toggleTutorialVideoCustomer",
        params
      );

      console.log("ressss: ", response);
      const { success } = response.data;
      if (!success) {
        return dispatch(
          showFailureNotification("Unable to link customer and tutorial video!")
        );
      }

      return success;
    } catch (error) {
      dispatch(showFailureNotification(error?.message));
      console.log(error);
    }
  },

  duplicatePlant: async (params, dispatch) => {
    try {
      const { data } = await proxy.duplicatePlant("duplicatePlant", params);

      // const { success } = response.data;
      if (!data.success) {
        return dispatch(
          showFailureNotification("Unable to duplicate the plant.")
        );
      }
      return data?.result;
    } catch (error) {
      dispatch(showFailureNotification(error?.data?.error.message));
      console.log(error);
    }
  },

  getConversations: async (params, dispatch) => {
    try {
      const result = await proxy.getConversations("getConversations", params);
      return result;
    } catch (error) {
      dispatch(showFailureNotification(error?.data?.error.message));
      console.log(error);
    }
  },

  deleteMessage: async (params, dispatch) => {
    try {
      const result = await proxy.deleteMessage("deleteMessage", params);
      return result;
    } catch (error) {
      dispatch(showFailureNotification(error?.data?.error.message));
      console.log(error);
    }
  },
  getNotificationImage: async (params, dispatch) => {
    try {
      const result = await proxy.getNotificationImage(
        "getNotificationImage",
        params
      );
      return result;
    } catch (error) {
      dispatch(showFailureNotification(error?.data?.error.message));
      console.log(error);
    }
  },
  getPermissions: async (params, dispatch) => {
    try {
      const response = await proxy.getPermissions("getPermissions", params);

      const { result, success } = response.data;
      if (!success) {
        return dispatch(
          showFailureNotification("Unable to get permission list!")
        );
      }

      return result;
    } catch (error) {
      dispatch(showFailureNotification(error?.data?.error.message));
      console.log(error);
    }
  },
  togglePermission: async (params, dispatch) => {
    try {
      const response = await proxy.togglePermission("togglePermission", params);
      const { result, success } = response?.data;
      if (!success) {
        return dispatch(
          showFailureNotification(`Error while toggling permission`)
        );
      }
      // dispatch(showSuccessNotification(`Successfully updated`));
      return result.customer_id;
    } catch (error) {
      if (error?.data?.error) {
        alert(error?.data?.error.message);
      }
      dispatch(showFailureNotification(`Error: ${JSON.stringify(error)}`));
    }
  },

  // PLANTDEX
  getPlantdexSubmissions: async (params, dispatch) => {
    try {
      const response = await proxy.getPlantdexSubmissions(
        "getPlantdexSubmissions",
        params
      );
      const { result, success } = response?.data;
      if (!success) {
        dispatch(showFailureNotification("Failed to create PlantDex."));
        return null;
      }
      dispatch(showSuccessNotification("PlantDex created successfully."));
      return result;
    } catch (error) {
      console.log(error);
      dispatch(
        showFailureNotification(
          `Error creating PlantDex: ${error.message || error}`
        )
      );
      return null;
    }
  },

  // Customer plant section type order

  getCustomerPlantSectionTypeOrder: async ({ customer_id }, dispatch) => {
    try {
      dispatch(startLoading());
      const response = await proxy.getCustomerPlantSectionTypeOrder(
        "getCustomerPlantSectionTypeOrder",
        { customer_id }
      );
      dispatch(finishLoading());
      console.log("Plant section type order response:", response);

      // Based on the response you shared, we need to extract data.result
      const result = response?.data?.result || [];

      return {
        success: true,
        data: result,
      };
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.data?.error?.message ||
            error?.message ||
            "Unable to fetch plant section type order"
          }`
        )
      );
      return { success: false, data: [] };
    }
  },

  updateCustomerPlantSectionTypeOrder: async (params, dispatch) => {
    try {
      const response = await proxy.updateCustomerPlantSectionTypeOrder(
        "updateCustomerPlantSectionTypeOrder",
        params
      );
      const { success, message } = response.data;
      if (!success) {
        dispatch(
          showFailureNotification(
            message || "Failed to update plant section type order"
          )
        );
        return { success: false };
      }
      dispatch(
        showSuccessNotification("Plant section type order saved successfully")
      );
      return { success: true };
    } catch (error) {
      console.error(error);
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.data?.error?.message ||
            error?.message ||
            "Unable to update plant section type order"
          }`
        )
      );
      return { success: false };
    }
  },

  listCustomerDomains: async ({ customer_id }, dispatch) => {
    try {
      const response = await proxy.get(`/v1/customers/${customer_id}/domains`);
      return response.data?.result ?? [];
    } catch (error) {
      dispatch(showFailureNotification("Unable to fetch SSO domains"));
      return [];
    }
  },

  addCustomerDomain: async (
    { customer_id, domain, identity_provider },
    dispatch
  ) => {
    try {
      const response = await proxy.post(
        `/v1/customers/${customer_id}/domains`,
        { domain, identity_provider }
      );
      const { success, message } = response.data?.result ?? response.data;
      if (!success) {
        dispatch(showFailureNotification(message || "Failed to add domain"));
        return false;
      }
      dispatch(showSuccessNotification("Domain added"));
      return true;
    } catch (error) {
      dispatch(showFailureNotification("Failed to add domain"));
      return false;
    }
  },

  removeCustomerDomain: async ({ customer_id, domain }, dispatch) => {
    try {
      const response = await proxy.delete(
        `/v1/customers/${customer_id}/domains/${encodeURIComponent(domain)}`
      );
      const { success, message } = response.data?.result ?? response.data;
      if (!success) {
        dispatch(showFailureNotification(message || "Failed to remove domain"));
        return false;
      }
      dispatch(showSuccessNotification("Domain removed"));
      return true;
    } catch (error) {
      dispatch(showFailureNotification("Failed to remove domain"));
      return false;
    }
  },

  // Field Clip Methods
  getTutorialVideos: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getTutorialVideos(
        "getTutorialVideos",
        params
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to fetch field bite"
          )
        );
        return [];
      }
      return response.data || [];
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to fetch field bite"
          }`
        )
      );
      return [];
    }
  },

  getTutorialVideo: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getTutorialVideo("getTutorialVideo", params);
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to fetch tutorial video"
          )
        );
        return null;
      }
      return response.data || null;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to fetch tutorial video"
          }`
        )
      );
      return null;
    }
  },

  createTutorialVideo: async (params, dispatch, formData) => {
    dispatch(startLoading());
    try {
      const response = await proxy.createTutorialVideo(
        "createTutorialVideo",
        params,
        formData
      );
      dispatch(finishLoading());
      console.log("Create tutorial video API response:", response);

      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to create tutorial video"
          )
        );
        return null;
      }

      dispatch(showSuccessNotification("Tutorial video created successfully"));

      // Extract the tutorial_video_id from the response
      const result = response.data?.result || response.data;
      console.log("Processed tutorial video result:", result);

      return result;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to create tutorial video"
          }`
        )
      );
      return null;
    }
  },

  duplicateTutorialVideo: async (id, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.duplicateTutorialVideo(
        "duplicateTutorialVideo",
        id
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to duplicate tutorial video"
          )
        );
        return null;
      }

      dispatch(
        showSuccessNotification("Tutorial video duplicated successfully")
      );

      const result = response.data?.result || response.data;

      return result;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to duplicate tutorial video"
          }`
        )
      );
      return null;
    }
  },

  updateTutorialVideo: async (params, dispatch, formData) => {
    dispatch(startLoading());
    try {
      const response = await proxy.updateTutorialVideo(
        "updateTutorialVideo",
        params,
        formData
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to update tutorial video"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Tutorial video updated successfully"));
      return response.data || null;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to update tutorial video"
          }`
        )
      );
      return null;
    }
  },

  deleteTutorialVideo: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.deleteTutorialVideo(
        "deleteTutorialVideo",
        params
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to delete tutorial video"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Tutorial video deleted successfully"));
      return response.data || { success: true };
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to delete tutorial video"
          }`
        )
      );
      return null;
    }
  },

  // Environmental Tailgate Methods
  getEnvironmentalTailgates: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getEnvironmentalTailgates(
        "getEnvironmentalTailgates",
        params
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to fetch field notes"
          )
        );
        return { success: false, data: [] };
      }

      return {
        success: true,
        result: response?.data?.result || response?.data || [],
      };
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to fetch field notes"
          }`
        )
      );
      return { success: false, data: [] };
    }
  },

  getEnvironmentalTailgate: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getEnvironmentalTailgate(
        "getEnvironmentalTailgate",
        params
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to fetch field note"
          )
        );
        return { success: false, data: null };
      }

      return {
        success: true,
        result: response?.data?.result || response?.data || null,
      };
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to fetch field note"
          }`
        )
      );
      return { success: false, data: null };
    }
  },

  createEnvironmentalTailgate: async (params, dispatch, formData) => {
    dispatch(startLoading());
    try {
      const response = await proxy.createEnvironmentalTailgate(
        "createEnvironmentalTailgate",
        params,
        formData
      );
      dispatch(finishLoading());
      console.log("Create environmental tailgate API response:", response);

      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to create field note"
          )
        );
        return null;
      }

      dispatch(showSuccessNotification("Field note created successfully"));

      // Extract environmental_tailgate_id from response
      const result = response.data?.result || response.data;
      console.log("Processed environmental tailgate result:", result);

      return result;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to create field note"
          }`
        )
      );
      return null;
    }
  },

  duplicateEnvironmentalTailgate: async (id, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.duplicateEnvironmentalTailgate(
        "duplicateEnvironmentalTailgate",
        id
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to duplicate field note"
          )
        );
        return null;
      }

      dispatch(showSuccessNotification("Field note duplicated successfully"));

      const result = response.data?.result || response.data;

      return result;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to duplicate field note"
          }`
        )
      );
      return null;
    }
  },

  updateEnvironmentalTailgate: async (params, dispatch, formData) => {
    dispatch(startLoading());
    try {
      const response = await proxy.updateEnvironmentalTailgate(
        "updateEnvironmentalTailgate",
        params,
        formData
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to update field note"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Field note updated successfully"));
      return response.data || null;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to update field note"
          }`
        )
      );
      return null;
    }
  },

  deleteEnvironmentalTailgate: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.deleteEnvironmentalTailgate(
        "deleteEnvironmentalTailgate",
        params
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to delete field note"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Field note deleted successfully"));
      return response.data || { success: true };
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to delete field note"
          }`
        )
      );
      return null;
    }
  },

  getEnvironmentalTailgateCustomers: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getEnvironmentalTailgateCustomers(
        "getEnvironmentalTailgateCustomers",
        params
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to fetch customers"
          )
        );
        return { success: false, data: [] };
      }

      return {
        success: true,
        result: response?.data?.result || response?.data || [],
      };
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to fetch customers"
          }`
        )
      );
      return { success: false, data: [] };
    }
  },

  toggleEnvironmentalTailgateCustomer: async (params, dispatch) => {
    try {
      const response = await proxy.toggleEnvironmentalTailgateCustomer(
        "toggleEnvironmentalTailgateCustomer",
        params
      );

      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to update customer assignment"
          )
        );
        return false;
      }

      dispatch(
        showSuccessNotification("Customer assignment updated successfully")
      );
      return true;
    } catch (error) {
      console.error(error);
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.data?.error?.message ||
            error?.message ||
            "Unable to update customer assignment"
          }`
        )
      );
      return false;
    }
  },

  registerEnvironmentalTailgateFile: async (params, dispatch) => {
    try {
      const { environmental_tailgate_id, ...rest } = params;
      const response = await createCall(
        `${apiUrl}/v1/environmental-tailgates/${environmental_tailgate_id}/register-upload`,
        methods.POST,
        rest
      );
      return response.data?.environmentalTailgate || null;
    } catch (error) {
      console.error(error);
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to register uploaded file"
          }`
        )
      );
      return null;
    }
  },

  // Survey Methods
  getSurveys: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getSurveys("getSurveys", params);
      dispatch(finishLoading());
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(showFailureNotification("Unable to fetch field bites"));
      return null;
    }
  },

  getSurvey: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getSurvey("getSurvey", params);
      dispatch(finishLoading());
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(showFailureNotification("Unable to fetch field bite"));
      return null;
    }
  },

  createSurvey: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.createSurvey("createSurvey", params);
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to create field bite"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Field bite created successfully"));
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to create field bite"
          }`
        )
      );
      return null;
    }
  },

  uploadFieldBitePackage: async (surveyId, file, dispatch) => {
    dispatch(startLoading());
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await createCall(
        `${apiUrl}/v1/surveys/${surveyId}/upload-package`,
        methods.POST,
        formData
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to upload package"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Package uploaded successfully"));
      return response.data?.result || null;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to upload package"
          }`
        )
      );
      return null;
    }
  },

  downloadFieldBitePackage: async (surveyId, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await createCall(
        `${apiUrl}/v1/surveys/${surveyId}/download-package`,
        methods.GET,
        null,
        { responseType: "blob" }
      );
      dispatch(finishLoading());
      triggerBlobDownload(response.data, `field-bite-${surveyId}.zip`);
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to download package"
          }`
        )
      );
    }
  },

  duplicateSurvey: async (id, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.duplicateSurvey("duplicateSurvey", id);
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to duplicate field bite"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Field bite duplicated successfully"));
      return response.data?.result || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to duplicate field bite"
          }`
        )
      );
      return null;
    }
  },

  updateSurvey: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.updateSurvey("updateSurvey", params);
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to update field bite"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Field bite updated successfully"));
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to update field bite"
          }`
        )
      );
      return null;
    }
  },

  deleteSurvey: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.deleteSurvey("deleteSurvey", params);
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to delete field bite"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Field bite deleted successfully"));
      return response.data || { success: true };
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to delete field bite"
          }`
        )
      );
      return null;
    }
  },

  getSurveyCustomers: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getSurveyCustomers(
        "getSurveyCustomers",
        params
      );
      dispatch(finishLoading());
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(showFailureNotification("Unable to fetch field bite customers"));
      return null;
    }
  },

  toggleSurveyCustomer: async (params, dispatch) => {
    try {
      const response = await proxy.toggleSurveyCustomer(
        "toggleSurveyCustomer",
        params
      );
      if (response?.data?.success) {
        dispatch(
          showSuccessNotification(
            response.data.message || "Customer link updated"
          )
        );
        return response.data.success;
      }
      return false;
    } catch (error) {
      console.log(error);
      dispatch(showFailureNotification("Unable to update customer link"));
      return false;
    }
  },

  // VMU course methods
  getVmuCourses: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getVmuCourses("getVmuCourses", params);
      dispatch(finishLoading());
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(showFailureNotification("Unable to fetch courses"));
      return null;
    }
  },

  getVmuCourse: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getVmuCourse("getVmuCourse", params);
      dispatch(finishLoading());
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(showFailureNotification("Unable to fetch course"));
      return null;
    }
  },

  createVmuCourse: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.createVmuCourse("createVmuCourse", params);
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to create course"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Course created successfully"));
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to create course"
          }`
        )
      );
      return null;
    }
  },

  updateVmuCourse: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.updateVmuCourse("updateVmuCourse", params);
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to update course"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Course updated successfully"));
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to update course"
          }`
        )
      );
      return null;
    }
  },

  deleteVmuCourse: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.deleteVmuCourse("deleteVmuCourse", params);
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to delete course"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Course deleted successfully"));
      return response.data || { success: true };
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to delete course"
          }`
        )
      );
      return null;
    }
  },

  uploadVmuCoursePackage: async (courseId, file, dispatch) => {
    dispatch(startLoading());
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await createCall(
        `${apiUrl}/v1/vmu/${courseId}/upload-package`,
        methods.POST,
        formData
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to upload package"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Package uploaded successfully"));
      return response.data?.result || null;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to upload package"
          }`
        )
      );
      return null;
    }
  },

  downloadVmuCoursePackage: async (courseId, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await createCall(
        `${apiUrl}/v1/vmu/${courseId}/download-package`,
        methods.GET,
        null,
        { responseType: "blob" }
      );
      dispatch(finishLoading());
      triggerBlobDownload(response.data, `vmu-course-${courseId}.zip`);
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to download package"
          }`
        )
      );
    }
  },

  deleteVmuCoursePackage: async (courseId, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await createCall(
        `${apiUrl}/v1/vmu/${courseId}/package`,
        methods.DELETE
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to delete package"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Package deleted successfully"));
      return response.data || { success: true };
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to delete package"
          }`
        )
      );
      return null;
    }
  },

  getVmuCourseAccounts: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getVmuCourseAccounts(
        "getVmuCourseAccounts",
        params
      );
      dispatch(finishLoading());
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(showFailureNotification("Unable to fetch course accounts"));
      return null;
    }
  },

  toggleVmuCourseAccount: async (params, dispatch) => {
    try {
      const response = await proxy.toggleVmuCourseAccount(
        "toggleVmuCourseAccount",
        params
      );
      if (response?.data?.success) {
        dispatch(
          showSuccessNotification(response.data.message || "Access updated")
        );
        return response.data.success;
      }
      return false;
    } catch (error) {
      console.log(error);
      dispatch(showFailureNotification("Unable to update access"));
      return false;
    }
  },

  // Bulk-assign accounts to a course (create flow); grants + one batched push.
  assignVmuCourseAccounts: async (courseId, accountIds, dispatch) => {
    try {
      const response = await createCall(
        `${apiUrl}/v1/vmu/${courseId}/assign-accounts`,
        methods.POST,
        { account_ids: accountIds }
      );
      return response.data || { success: true };
    } catch (error) {
      console.error(error);
      dispatch(showFailureNotification("Unable to assign users to course"));
      return null;
    }
  },

  // VMU customers / settings methods
  getVmuCustomers: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getVmuCustomers("getVmuCustomers", params);
      dispatch(finishLoading());
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(showFailureNotification("Unable to fetch VMU customers"));
      return null;
    }
  },

  getVmuCustomer: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getVmuCustomer("getVmuCustomer", params);
      dispatch(finishLoading());
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(showFailureNotification("Unable to fetch VMU settings"));
      return null;
    }
  },

  updateVmuSettings: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.updateVmuSettings(
        "updateVmuSettings",
        params
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to update VMU settings"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("VMU settings updated successfully"));
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to update VMU settings"
          }`
        )
      );
      return null;
    }
  },

  // VMU resource methods
  getVmuResources: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getVmuResources("getVmuResources", params);
      dispatch(finishLoading());
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(showFailureNotification("Unable to fetch resources"));
      return null;
    }
  },

  createVmuResource: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.createVmuResource(
        "createVmuResource",
        params
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to create resource"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Resource created successfully"));
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to create resource"
          }`
        )
      );
      return null;
    }
  },

  updateVmuResource: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.updateVmuResource(
        "updateVmuResource",
        params
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to update resource"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Resource updated successfully"));
      return response.data || response;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to update resource"
          }`
        )
      );
      return null;
    }
  },

  deleteVmuResource: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.deleteVmuResource(
        "deleteVmuResource",
        params
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to delete resource"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Resource deleted successfully"));
      return response.data || { success: true };
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to delete resource"
          }`
        )
      );
      return null;
    }
  },

  uploadVmuResourcePackage: async (resourceId, file, dispatch) => {
    dispatch(startLoading());
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await createCall(
        `${apiUrl}/v1/vmu-resources/${resourceId}/upload-package`,
        methods.POST,
        formData
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to upload package"
          )
        );
        return null;
      }
      dispatch(showSuccessNotification("Package uploaded successfully"));
      return response.data?.result || null;
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to upload package"
          }`
        )
      );
      return null;
    }
  },

  downloadVmuResourcePackage: async (resourceId, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await createCall(
        `${apiUrl}/v1/vmu-resources/${resourceId}/download-package`,
        methods.GET,
        null,
        { responseType: "blob" }
      );
      dispatch(finishLoading());
      triggerBlobDownload(response.data, `vmu-resource-${resourceId}.zip`);
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to download package"
          }`
        )
      );
    }
  },

  // Presigned URL methods for direct client-to-S3 uploads
  getPresignedUrl: async (params, dispatch) => {
    try {
      const response = await createCall(
        `${apiUrl}/v1/storage/presigned-url`,
        methods.POST,
        params
      );
      return response.data || null;
    } catch (error) {
      console.error(error);
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to get presigned URL"
          }`
        )
      );
      return null;
    }
  },

  getMultipartUploadUrls: async (params, dispatch) => {
    try {
      const response = await createCall(
        `${apiUrl}/v1/storage/multipart-upload`,
        methods.POST,
        params
      );
      return response.data || null;
    } catch (error) {
      console.error(error);
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to get multipart upload URLs"
          }`
        )
      );
      return null;
    }
  },

  completeMultipartUpload: async (params, dispatch) => {
    try {
      const response = await createCall(
        `${apiUrl}/v1/storage/complete-multipart`,
        methods.POST,
        params
      );
      return response.data || null;
    } catch (error) {
      console.error(error);
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to complete multipart upload"
          }`
        )
      );
      return null;
    }
  },

  registerUploadedFile: async (params, dispatch) => {
    try {
      const { tutorial_video_id, ...rest } = params;
      const response = await createCall(
        `${apiUrl}/v1/tutorial-videos/${tutorial_video_id}/register-upload`,
        methods.POST,
        rest
      );
      return response.data?.tutorialVideo || null;
    } catch (error) {
      console.error(error);
      dispatch(
        showFailureNotification(
          `Error: ${
            error?.response?.data?.message ||
            error?.message ||
            "Unable to register uploaded file"
          }`
        )
      );
      return null;
    }
  },

  getTutorialVideoCustomers: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getTutorialVideoCustomers(
        "getTutorialVideoCustomers",
        params
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to fetch customers"
          )
        );
        return [];
      }
      return response?.data || [];
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(showFailureNotification("Unable to fetch customers"));
      return [];
    }
  },

  getTutorialVideoCustomers: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getTutorialVideoCustomers(
        "getTutorialVideoCustomers",
        params
      );
      dispatch(finishLoading());
      if (response?.data?.success === false) {
        dispatch(
          showFailureNotification(
            response.data.message || "Failed to fetch customers"
          )
        );
        return [];
      }
      return response?.data || [];
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(showFailureNotification("Unable to fetch customers"));
      return [];
    }
  },

  getSurveysReport: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getSurveysReport("getSurveysReport", params);
      dispatch(finishLoading());
      return response.data?.result || [];
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification("Unable to generate field bites report")
      );
      return [];
    }
  },

  getEnvironmentalTailgatesReport: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getEnvironmentalTailgatesReport(
        "getEnvironmentalTailgatesReport",
        params
      );
      dispatch(finishLoading());
      return response.data?.result || [];
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification("Unable to generate field notes report")
      );
      return [];
    }
  },

  getTutorialVideosReport: async (params, dispatch) => {
    dispatch(startLoading());
    try {
      const response = await proxy.getTutorialVideosReport(
        "getTutorialVideosReport",
        params
      );
      dispatch(finishLoading());
      return response.data?.result || [];
    } catch (error) {
      console.error(error);
      dispatch(finishLoading());
      dispatch(
        showFailureNotification("Unable to generate field clips report")
      );
      return [];
    }
  },
};

export default ApiService;

// Direct API methods
export const getTagsList = async (params) => {
  try {
    const response = await proxy.getTags("getTags", params);
    const result = response?.data?.result;
    return Array.isArray(result) ? result : result?.tags || [];
  } catch (error) {
    console.log("Error fetching tags: ", error);
    return [];
  }
};

export const createTagDirect = async (params) => {
  try {
    // Validate required fields
    if (!params.name || !params.customer_id) {
      return {
        success: false,
        error: { message: "Tag name and customer ID are required" },
      };
    }

    const response = await proxy.createTag("createTag", params);
    const { result, success, error } = response?.data;

    if (!success) {
      console.log("Error creating tag:", error);
      return { success: false, error };
    }

    return { success: true, tag: result };
  } catch (error) {
    console.log("Error creating tag: ", error);
    return {
      success: false,
      error: {
        message:
          error?.data?.error?.message ||
          error?.message ||
          "An unexpected error occurred while creating tag",
      },
    };
  }
};

export const updateTagDirect = async (params) => {
  try {
    // Validate required fields
    if (!params.name) {
      return {
        success: false,
        error: { message: "Tag name is required" },
      };
    }

    const response = await proxy.updateTag("updateTag", params);
    const { result, success, error } = response?.data;

    if (!success) {
      console.log("Error updating tag:", error);
      return { success: false, error };
    }

    return { success: true, tag: result };
  } catch (error) {
    console.log("Error updating tag: ", error);
    return {
      success: false,
      error: {
        message:
          error?.data?.error?.message ||
          error?.message ||
          "An unexpected error occurred while updating tag",
      },
    };
  }
};

export const deleteTagDirect = async (tagId) => {
  try {
    // Ensure tagId is a number
    const numericTagId = Number(tagId);

    if (isNaN(numericTagId)) {
      console.log("Invalid tag_id: not a number");
      return {
        success: false,
        error: { message: "Tag ID must be a valid number" },
      };
    }

    const response = await proxy.deleteTag("deleteTag", {
      tag_id: numericTagId,
    });
    const { success, error } = response?.data;

    if (!success && error) {
      console.log("API Error deleting tag:", error);
      return { success: false, error };
    }

    return { success: success || false };
  } catch (error) {
    console.log("Error deleting tag: ", error);
    return {
      success: false,
      error: {
        message:
          error?.data?.error?.message ||
          error?.message ||
          "An unexpected error occurred",
      },
    };
  }
};
